// src/data/editions/insurance.js
//
// INSURANCE edition — the original workforce / disability-insurer skin
// (Alberta Blue Cross). Leads with STD/LTD claims avoidance, because for an
// insurer those claims are the P&L. This module is the source of truth for the
// insurer vertical; components read from it via useEdition().

const insurance = {
    id: 'insurance',
    label: 'Workforce (Insurance)',

    palette: { deep: '#00205B', blue: '#00a0d2', yellow: '#00a0d2' },

    // Console header titles per tab (EnterpriseConsole.getPageTitle).
    pageTitles: {
        'cc-overview': 'Claims & Risk Overview',
        'cc-triangulation': 'Triangulation: Members, the Plan Sponsor, the Average',
        'cc-data': 'Data Intake: The Plan Sponsor’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Readiness & Strain',
        'cc-predictive': 'STD & LTD Claims Forecast',
        'cc-roi': 'Benefits ROI & Claims Avoidance',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Workforce Risk Command Center'
    },

    // Legend label for the second series on the Overview trend chart.
    trendSignalLabel: 'STD claim signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge (T0.3)
        capacityScore: 82,
        capacityTrend: 3,
        workforce: 1200,
        highSignalCohort: 142,
        topPriorityGroup: 'Claims Adjudication',
        topPulseTheme: 'Workload',
        participationRate: 64,
        modeledClaimsAvoidance: '$2.1M',
        modeledStdClaims: { low: 20, high: 32, windowDays: 90 },
        modeledLtdTransition: { low: 6, high: 10 },
        leaveProbability: { mid: 18, low: 14, high: 22 },
        // Overview roll-up card copy (2nd + 3rd cards).
        outlookTitle: 'Claims Outlook',
        outlookNoun: 'STD claims',
        outlookUnit: 'claims',
        financialNoun: 'claims-avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 79, signal: 22 },
        { date: '4th', capacity: 80, signal: 21 },
        { date: '8th', capacity: 78, signal: 24 },
        { date: '12th', capacity: 81, signal: 20 },
        { date: '16th', capacity: 80, signal: 21 },
        { date: '20th', capacity: 82, signal: 18 },
        { date: '24th', capacity: 81, signal: 19 },
        { date: '28th', capacity: 82, signal: 18 },
        { date: 'Today', capacity: 82, signal: 18 }
    ],

    readiness: {
        scoreLabel: 'Enterprise Capacity Score',
        signalSubtext: 'caseload density + scheduled meeting load',
        groups: [
            { name: 'Claims Adjudication', pressure: 79, note: 'Sustained caseload load', responses: 96 },
            { name: 'Customer Experience', pressure: 74, note: 'Elevated contact volume', responses: 41 },
            { name: 'Underwriting', pressure: 72, note: 'Steady throughput, rising hours', responses: 12 },
            { name: 'Provider Relations', pressure: 68, note: 'Within normal range', responses: 18 },
            { name: 'Actuarial Sciences', pressure: 63, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported strain trend', value: '+5%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Sick leave & absenteeism', value: '+7%', tier: 'Lagging', tone: 'amber' },
            { label: 'Missed deadlines / delayed OKRs', value: '+4%', tier: 'Lagging', tone: 'amber' },
            { label: 'Manager check-in signal', value: 'Steady', tier: 'Intermediate', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Organization-wide', responses: 312 },
            { id: 'operations', label: 'Operations', responses: 84 },
            { id: 'cx', label: 'Customer Experience', responses: 41 },
            { id: 'underwriting', label: 'Underwriting', responses: 12 },
            { id: 'actuarial', label: 'Actuarial Sciences', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Workload', sentiment: 'risk', scope: 'operations',
              text: 'The volume this week is brutal. We spend more time fighting the legacy system than actually helping plan members.' },
            { id: 2, time: '5 hours ago', theme: 'Tools', sentiment: 'positive', scope: 'underwriting',
              text: 'The new risk-assessment pipeline is saving hours of manual data entry. Good investment by leadership.' },
            { id: 3, time: 'Yesterday', theme: 'Culture', sentiment: 'positive', scope: 'operations',
              text: 'Communication around the new fee structures has been much clearer this month. The transparency is appreciated.' },
            { id: 4, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'cx',
              text: 'Call times are climbing and the team is underwater. We need more coverage on the phones.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'cx',
              text: 'Felt genuinely supported when my manager flagged my workload before I had to ask.' }
        ],
        sentiment: {
            positiveSummary: 'Strong response to recent transparency in leadership communication and tooling investment.',
            emergingTheme: 'Workload volume and legacy-system friction are the most frequent sources of frustration.'
        }
    },

    predictive: {
        title: 'The Disability-Claims Funnel',
        intro: 'In the modeled baseline, an estimated 14-22% of the high-readiness-risk cohort would be expected to file a Short-Term Disability (STD) claim within 90 days. This is a directional projection from leading signals - not a forecast - and it needs a 6-12 month window and a comparison baseline before any causal claim.',
        lever: 'Earlier support routing',
        ownLabel: null,
        tiers: [
            { value: '1,200', label: 'Total workforce', source: 'observed', tone: 'neutral', width: 100 },
            { value: '142', label: 'High-readiness-risk cohort', source: 'observed', tone: 'brand', width: 88 },
            { value: '20-32', label: 'Projected STD claims', source: 'modeled', tone: 'amber', width: 72 },
            { value: '6-10', label: 'Projected LTD transition', source: 'modeled', tone: 'amber', width: 56 }
        ],
        cards: [
            { title: 'Capacity-Claims Association', value: '~4.2x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Populations sustaining low capacity scores show a higher rate of stress-related STD claims. In comparable modeled populations the association is roughly 4.2x - directional, and confirmed only against an employer baseline.' },
            { title: 'Modeled Leave Probability', value: '18%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 14-22% band',
              body: 'Modeled probability that an unmanaged stress claim originates from a high-load group this quarter. The band widens or narrows as employer baseline data is added.' },
            { title: 'Proactive Utilization Lift', value: '+34%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Cohorts engaged with AI Coach recommendations show higher proactive routing into existing EFAP / paramedical benefits - a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Projections are conservatively attributed and reported at the group level only. Claims, leave and LTD transitions are lagging outcomes that require a longer window and a comparison baseline before any causal claim. EAS reports what it measures and labels what it models.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        // Only the bits that differ by vertical; the component keeps neutral defaults for the rest.
        replacementMultiple: 0.5,
        turnoverRate: 12,
        termLabels: {
            lostTime: 'Avoided Lost Time',
            lostTimeBasis: 'Baseline absence days x loaded daily cost x modeled reduction.'
        }
    },

    copilot: { exampleScope: 'Operations' }
};

export default insurance;
